import {
  useCallback,
  useRef,
  useMemo,
  useImperativeHandle,
  useState,
  useEffect,
  MutableRefObject,
  FC,
} from 'react';
import {
  Editor,
  useEditor as useTipTapMarkdownEditor,
  type UseEditorOptions,
} from '@tiptap/react';
import { Extension } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import { useSelector } from 'react-redux';
import TTStrike from '@tiptap/extension-strike';
import TTItalic from '@tiptap/extension-italic';
import TTBold from '@tiptap/extension-bold';
import TTDocument from '@tiptap/extension-document';
import TTParagraph from '@tiptap/extension-paragraph';
import TTText from '@tiptap/extension-text';
import TTHeading from '@tiptap/extension-heading';
import TTCode from '@tiptap/extension-code';
import TTCodeBlock from '@tiptap/extension-code-block';
import {
  OrderedList as TTOrderedList,
  ListItem as TTListItem,
  BulletList as TTBulletList,
} from '@tiptap/extension-list';
import { ListKeymap as TTListKeymap } from '@tiptap/extension-list/keymap';
import {
  UndoRedo as TTUndoRedo,
  CharacterCount as TTCharacterCount,
} from '@tiptap/extensions';
import { MarkdownHorizontalRule } from './extensions/markdownHorizontalRule';
import { MarkdownHardBreak } from './extensions/markdownHardBreak';
import { MarkdownLink } from './extensions/markdownLink';
import { MarkdownToolBar } from 'components/StandaloneToolBar/presets/MarkdownToolbar';
import { usePropCallback } from '../editor';
import { StyledEditorContent } from '../styles';
import { MarkdownEditorContext } from './context';
import { MarkdownEditorContainer } from './components/MarkdownEditorContainer';
import { getHTMLValue, getRawTextValue } from './helpers';
import { SCROLL_FADE_LENGTH } from './constants';
import { WYSIWYGAIContext, useWYSIWYGAIContext } from '../ai-context';
import { getChosenBusiness } from 'store/authentication/selectors';

const RawTextCharacterCount = TTCharacterCount.extend({
  onBeforeCreate() {
    this.storage.characters = (options) => {
      const node = options?.node || this.editor.state.doc;
      return node.textBetween(0, node.content.size, '\n').length;
    };
  },
});

const NEWLINES_REGEX = /\s*[\r\n]+\s*/g;

const SingleLine = Extension.create({
  name: 'singleLine',
  addKeyboardShortcuts() {
    return {
      Enter: () => true,
      'Shift-Enter': () => true,
      'Mod-Enter': () => true,
    };
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handlePaste: (view, event) => {
            const text = event.clipboardData?.getData('text/plain');
            if (text === undefined) {
              return false;
            }
            view.dispatch(
              view.state.tr.insertText(text.replace(NEWLINES_REGEX, ' '))
            );
            return true;
          },
        },
      }),
    ];
  },
});

type MarkdownEditorProps = {
  isMarkdown?: boolean;
  onInit?: UseEditorOptions['onCreate'];
  onChange?: UseEditorOptions['onUpdate'];
  onFocus?: UseEditorOptions['onFocus'];
  onBlur?: UseEditorOptions['onBlur'];
  onSave?: (value: string) => void;
  initialValue?: string;
  autofocus?: UseEditorOptions['autofocus'];
  readonly?: boolean;
  editorRef?:
    | MutableRefObject<MarkdownHandle | null>
    | ((markdownHandle: MarkdownHandle) => void);
  initialHeight?: number;
  initialWidth?: number;
  maxLength?: number;
  singleLine?: boolean;
};

export type MarkdownHandle = {
  containerRef: MutableRefObject<HTMLDivElement | null>;
  editor: Editor;
};

export const MarkdownEditor: FC<MarkdownEditorProps> = ({
  isMarkdown = true,
  onInit,
  onChange,
  onFocus,
  onBlur,
  onSave,
  initialValue = '',
  autofocus = false,
  readonly = false,
  editorRef,
  initialHeight,
  initialWidth,
  maxLength, // ignored for isMarkdown
  singleLine = false, // ignored for isMarkdown
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const initialValueRef = useRef<string>(initialValue);
  const onChangeCB = usePropCallback(onChange);
  const onFocusCB = usePropCallback(onFocus);
  const onBlurCB = usePropCallback(onBlur);
  const onInitCB = usePropCallback(onInit);
  const onSaveCB = usePropCallback(onSave);

  const [converting, setConverting] = useState(false);

  const subscriptionsOnUpdate = useMemo(() => {
    const subs = new Map<string, UseEditorOptions['onUpdate']>();
    return subs;
  }, []);

  const subscriptionsOnSelectionUpdate = useMemo(() => {
    const subs = new Map<string, UseEditorOptions['onSelectionUpdate']>();
    return subs;
  }, []);

  const subscribeToUpdate = useCallback(
    (id: string, callback: UseEditorOptions['onUpdate']) => {
      subscriptionsOnUpdate.set(id, callback);
      return () => {
        subscriptionsOnUpdate.delete(id);
      };
    },
    [subscriptionsOnUpdate]
  );

  const subscribeToSelectionUpdate = useCallback(
    (id: string, callback: UseEditorOptions['onSelectionUpdate']) => {
      subscriptionsOnSelectionUpdate.set(id, callback);
      return () => {
        subscriptionsOnSelectionUpdate.delete(id);
      };
    },
    [subscriptionsOnSelectionUpdate]
  );

  const saveHandler = useCallback(
    async (editor: Editor) => {
      setConverting(true);
      const rawTextValue = await getRawTextValue(editor, isMarkdown);
      initialValueRef.current = rawTextValue;
      onSaveCB(rawTextValue);
      setConverting(false);
    },
    [onSaveCB, isMarkdown]
  );

  const editor = useTipTapMarkdownEditor(
    {
      onUpdate: (opts) => {
        onChangeCB(opts);
        subscriptionsOnUpdate.forEach((callback) => {
          callback?.(opts);
        });
      },
      onSelectionUpdate: (opts) => {
        opts.editor.commands.scrollIntoView();
        subscriptionsOnSelectionUpdate.forEach((callback) => {
          callback?.(opts);
        });
      },
      onFocus: onFocusCB,
      onBlur: onBlurCB,
      onCreate: onInitCB,
      extensions: isMarkdown
        ? [
            TTDocument,
            TTText,
            TTParagraph,
            TTHeading,
            TTUndoRedo,
            TTBold,
            TTItalic,
            TTStrike,
            TTListItem,
            TTOrderedList,
            TTBulletList,
            TTListKeymap,
            TTCode,
            TTCodeBlock,
            MarkdownLink,
            MarkdownHorizontalRule,
            MarkdownHardBreak,
          ]
        : [
            TTDocument,
            TTText,
            TTParagraph,
            ...(maxLength
              ? [RawTextCharacterCount.configure({ limit: maxLength })]
              : []),
            ...(singleLine ? [SingleLine] : []),
          ],
      content: isMarkdown ? '' : initialValue,
      autofocus,
      editable: !readonly,
      editorProps: {
        scrollThreshold: SCROLL_FADE_LENGTH,
        scrollMargin: SCROLL_FADE_LENGTH,
      },
    },
    [readonly, isMarkdown, maxLength, singleLine]
  );

  useImperativeHandle(
    editorRef,
    () => ({
      editor,
      containerRef,
    }),
    [editor]
  );

  useEffect(() => {
    if (editor) {
      const convertAction = async () => {
        setConverting(true);
        const html = await getHTMLValue(initialValueRef.current, isMarkdown);
        editor.commands.setContent(html, {
          parseOptions: {
            preserveWhitespace: !isMarkdown,
          },
        });

        setConverting(false);
      };

      if (initialValueRef.current) {
        convertAction();
      }
    }
  }, [editor, isMarkdown]);

  const handleSave = useCallback(() => {
    if (editor) {
      saveHandler(editor);
    }
  }, [editor, saveHandler]);

  const business = useSelector(getChosenBusiness);
  const { isDefaultContextValue } = useWYSIWYGAIContext();

  const editorContent = (
    <MarkdownEditorContext.Provider
      value={{ editor, subscribeToUpdate, subscribeToSelectionUpdate }}
    >
      {readonly || !isMarkdown ? null : <MarkdownToolBar />}
      <MarkdownEditorContainer
        ref={containerRef}
        onSave={handleSave}
        disabled={converting}
        readonly={readonly}
        isMarkdown={isMarkdown}
        initialHeight={initialHeight}
        initialWidth={initialWidth}
      >
        <StyledEditorContent
          editor={editor}
          enableResize
          className="ProseMirror"
        />
      </MarkdownEditorContainer>
    </MarkdownEditorContext.Provider>
  );

  // Wrap with AI context if we have a business ID and no existing context
  if (business?.id && isDefaultContextValue) {
    return (
      <WYSIWYGAIContext businessId={business.id}>
        {editorContent}
      </WYSIWYGAIContext>
    );
  }

  return editorContent;
};

MarkdownEditor.displayName = 'MarkdownEditor';
